/**
 * Admin Settings Dialog.
 *
 * Five tabs:
 *  - Facilities      (CRUD)
 *  - Dock Doors      (CRUD, per facility)
 *  - Capabilities    (CRUD)
 *  - Carriers        (read-only, sourced from Pipeline)
 *  - Clients         (read-only, sourced from Pipeline)
 *
 * Reachable only when user.isAdmin === true. App.tsx hides the gear icon
 * for non-admins; backend re-enforces on every CRUD call.
 */
import { useEffect, useState } from 'react';
import { Plus, RotateCcw, Trash2, Pencil, AlertCircle, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabPanel } from '@/components/ui/tabs';
import { useMe } from '@/auth/useMe';
import { Button } from '@/components/ui/button';
import { UsersSection } from '@/components/UsersSection';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import FacilityHoursAdmin from './FacilityHoursAdmin';
import ChatGroupsAdmin from './ChatGroupsAdmin';
import FacilityDirectionsAdmin from './FacilityDirectionsAdmin';
import StatusColorsAdmin from './StatusColorsAdmin';
import RailPanelAdmin from '@/components/RailPanelAdmin';
import BoardConfigAdmin from '@/components/BoardConfigAdmin';
import CheckInConfigAdmin from '@/components/CheckInConfigAdmin';
import DocumentConfigAdmin from '@/components/DocumentConfigAdmin';
import DoorTextConfigAdmin from '@/components/DoorTextConfigAdmin';
import OcrIdentifierRankAdmin from '@/components/OcrIdentifierRankAdmin';
import DepartureChecklistAdmin from '@/components/DepartureChecklistAdmin';
import HandoutAdmin from '@/components/HandoutAdmin';
import ConsigneeAdmin from '@/components/ConsigneeAdmin';
import { AppointmentFieldsAdmin } from '@/components/AppointmentFieldsAdmin';
import { AutomatedTextsAdmin } from '@/components/AutomatedTextsAdmin';
import { ReminderScheduleAdmin } from '@/components/ReminderScheduleAdmin';
import { CarrierUsersSection } from '@/components/CarrierUsersSection';
import { DriversSection } from '@/components/DriversSection';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    adminCreateCapability,
    adminCreateCarrier,
    adminCreateDoor,
    adminCreateFacility, adminListAvailableFacilities,
    adminDeleteCapability,
    adminListCapabilities,
    adminListCarriers,
    adminListDoors,
    adminListFacilities,
    adminRestoreCarrier,
    adminRestoreDoor,
    adminRestoreFacility,
    adminSoftDeleteCarrier,
    adminSoftDeleteDoor,
    adminSoftDeleteFacility,
    adminUpdateCapability,
    adminUpdateCarrier,
    adminUpdateDoor,
    adminUpdateFacility,
    getMe,
    ApiCallError,
    CapabilityAdminRow,
    CarrierAdminRow,
    DoorAdminRow,
    FacilityRow,
    UpsertCapabilityBody,
    UpsertCarrierBody,
    UpsertDoorBody,
    UpsertFacilityBody,
} from '@/api/client';
import type { LookupsDto } from '@/api/types';
import { cn } from '@/lib/utils';

/**
 * ⭐⭐ THE TAB VOCABULARY, AND IT IS EXPORTED BECAUSE App.tsx USED TO RETYPE IT.
 *
 * ⛔ Until 2026-09-09 this union existed TWICE — here, and as `SettingsTabId` in
 * App.tsx, neither importing the other. §1R.16: two statements of one fact drift
 * from the day they are duplicated. ⚠️ They HAD drifted: adding the Right-Side
 * Toolbar tab here and the gear item there failed the build with *"Type
 * 'rail-panels' is not assignable to type 'SettingsTabId'"*.
 *
 * ⭐ THIS FILE OWNS THE LIST because it renders the tabs. App.tsx imports it, so
 * the two can no longer disagree about what a tab id IS.
 *
 * ⚠️ IT STILL CANNOT PROVE A TAB HAS A MENU ITEM. A tab added here with no gear
 * entry is legal and invisible — which is the 2026-08-19 failure, and it is why
 * the "BOTH PLACES, ALWAYS" comments in App.tsx stay. ⛔ The compiler now stops
 * you naming a tab that does not exist; it cannot make you name one that does.
 */
export type TabId = 'facilities' | 'doors' | 'capabilities' | 'carriers' | 'clients' | 'users' | 'carrier-users' | 'drivers' | 'facility-hours' | 'appointment-limits' | 'door-dedications' | 'facility-directions' | 'chat-groups' | 'status-colors' | 'rail-panels' | 'board-screens' | 'driver-checkin' | 'documents' | 'door-text' | 'appointment-fields' | 'automated-texts' | 'reminder-schedule' | 'ocr-ranking' | 'departure-checklists' | 'handouts' | 'consignees';

interface SettingsDialogProps {
    open: boolean;
    onClose: () => void;
    lookups: LookupsDto;
    /**
     * ⭐ THE FACILITY THE CALENDAR IS SHOWING, so per-facility tabs open on the site
     * the user is actually working in rather than whichever is first alphabetically.
     * See the note on DocumentConfigAdmin's initialFacilityCode for what that cost.
     */
    currentFacilityCode?: string | null;
    getToken: () => Promise<string>;
    /** Called whenever the admin makes a change that might affect the calendar */
    onChanged: () => void;
    /** Optional initial tab to open. Defaults to 'doors' for backward compat. */
    initialTab?: TabId;
    /**
     * ⭐ F130 (Decision 308): the record to open on — a carrier / client / consignee code
     * from a double-click on its rail card. The Carriers tab opens that carrier's editor,
     * Clients filters to it, Consignees loads its form.
     */
    focusCode?: string | null;
}

export function SettingsDialog({ open, onClose, lookups, getToken, onChanged, initialTab, currentFacilityCode, focusCode = null }: SettingsDialogProps) {
    const [tab, setTab] = useState<TabId>(initialTab ?? 'doors');

    // If the dialog is re-opened with a different initialTab (e.g. user
    // clicked a different gear-menu item), switch tabs on the next open.
    useEffect(() => {
        if (open && initialTab) {
            setTab(initialTab);
        }
    }, [open, initialTab]);

    // Role-based tab visibility. Scheduler-allowed tabs vs admin-only tabs.
    // Mirrors the gear-menu gating in App.tsx and the backend requireGroup checks.
    const me = useMe();
    const isAdmin = me?.isAdmin ?? false;
    // ⚠️ MUST AGREE WITH GEAR_MENU's requiredRole in App.tsx. A gear item marked
    // 'scheduler' whose tab is not listed here opens the dialog and is immediately
    // bounced to facility-hours by the canSee() effect below — which reads as the
    // menu item being broken.
    const SCHEDULER_TABS: TabId[] = ['facility-hours', 'appointment-limits', 'door-dedications', 'facility-directions', 'carrier-users', 'drivers', 'appointment-fields', 'automated-texts', 'reminder-schedule'];
    const canSee = (t: TabId) => isAdmin || SCHEDULER_TABS.includes(t);

    // If a non-admin somehow lands on an admin-only tab, fall back to an allowed one.
    useEffect(() => {
        if (open && !canSee(tab)) {
            setTab('facility-hours');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tab, isAdmin]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle>Settings</DialogTitle>
                    <DialogDescription>
                        Manage facilities, doors, and capabilities. Changes take effect immediately.
                    </DialogDescription>
                </DialogHeader>

                <Tabs<TabId>
                    value={tab}
                    onChange={setTab}
                    tabs={([
                        { value: 'facilities', label: 'Facilities' },
                        { value: 'doors', label: 'Dock Doors' },
                        { value: 'capabilities', label: 'Capabilities' },
                        { value: 'carriers', label: 'Carriers' },
                        { value: 'clients', label: 'Clients' },
                        { value: 'users', label: 'Users' },
                        { value: 'facility-hours', label: 'Facility Hours' },
                        { value: 'appointment-limits', label: 'Appointments' },
                        { value: 'door-dedications', label: 'Door Dedications' },
                        { value: 'chat-groups', label: 'Chat Groups' },
						{ value: 'facility-directions', label: 'Directions' },
                        // Carrier Portal Users had a panel and a gear-menu entry
                        // but no tab button, so opening it from the gear showed
                        // the panel with nothing selected in the strip and no way
                        // to navigate back to it.
                        { value: 'carrier-users', label: 'Portal Users (Carriers & Clients)' },
                        { value: 'drivers', label: 'Drivers' },
                        { value: 'status-colors', label: 'Status Colors' },
                        { value: 'rail-panels', label: 'Right-Side Toolbar' },
                        { value: 'board-screens', label: 'Dockview Screens' },
                        { value: 'driver-checkin', label: 'Driver Check-in' },
                        { value: 'documents', label: 'Documents' },
                        // ⭐ NEXT TO Documents, deliberately. That tab decides how a scan
                        // is NAMED and stored; this one which number on it is used to find
                        // the load. Same paperwork, two different Save buttons.
                        { value: 'ocr-ranking', label: 'OCR Ranking' },
                        // ⭐ #317 — NEXT TO Documents AND OCR Ranking, deliberately. Those
                        // two decide how a scan is named and how it is matched to a load;
                        // this one decides which scans a load is not allowed to leave
                        // without. Same paperwork, read one after the other.
                        { value: 'departure-checklists', label: 'Departure Checklists' },
                        // ⭐ #317 step 5 — NEXT TO the checklists, because a `standing`
                        // requirement on one looks up a handout on the other by document
                        // type. Setting up the first without the second produces a
                        // requirement nothing can satisfy.
                        { value: 'handouts', label: 'Handouts' },
                        // ⭐ #324 — NEXT TO Handouts, deliberately. A handout is
                        // scoped BY consignee code, and the delivery pin resolves the
                        // same code to an address. Setting up one without the other is
                        // how a handout ends up scoped to a place nothing can find.
                        { value: 'consignees', label: 'Consignees' },
                        // ⭐ FIRST IN THE ADMIN GROUP WOULD BE WRONG — it is the least
                        // often changed screen here. Last, next to the other things that
                        // are set once during onboarding.
                        { value: 'door-text', label: 'Dock Door Text' },
                        { value: 'appointment-fields', label: 'Appointment Fields' },
                        { value: 'automated-texts', label: 'Automated Texts' },
                        // ⭐ NEXT TO Automated Texts, deliberately. That tab decides
                        // WHETHER we send unasked; this one WHEN and WHAT. Adjacent
                        // because they are read together, separate because their Save
                        // buttons do very different things.
                        { value: 'reminder-schedule', label: 'Reminder Schedule' },
                    ] as { value: TabId; label: string }[]).filter(t => canSee(t.value))}
                />

                <div className="min-h-[400px] max-h-[60vh] overflow-y-auto">
                    <TabPanel when={tab === 'facilities'}>
                        <FacilitiesSection getToken={getToken} onChanged={onChanged} />
                    </TabPanel>
                    <TabPanel when={tab === 'doors'}>
                        <DoorsSection getToken={getToken} onChanged={onChanged} />
                    </TabPanel>
                    <TabPanel when={tab === 'capabilities'}>
                        <CapabilitiesSection getToken={getToken} onChanged={onChanged} />
                    </TabPanel>
                    <TabPanel when={tab === 'carriers'}>
                        <CarriersSection getToken={getToken} onChanged={onChanged} focusCode={open && tab === 'carriers' ? focusCode : null} />
                    </TabPanel>
                    <TabPanel when={tab === 'clients'}>
                        <ReadOnlyList
                            title="Clients"
                            note="Clients (Business Units) live in Pipeline. Edit them there; they appear here as soon as Pipeline syncs."
                            rows={lookups.clients.map((c) => ({ code: c.ClientCode, name: c.ClientName }))}
                            initialFilter={open && tab === 'clients' ? focusCode : null}
                        />
                    </TabPanel>
                    <TabPanel when={tab === 'users'}>
                        <UsersSection getToken={getToken} onChanged={onChanged} />
                    </TabPanel>
                    <TabPanel when={tab === 'carrier-users'}>
                        <CarrierUsersSection getToken={getToken} onChanged={onChanged} />
                    </TabPanel>
                    <TabPanel when={tab === 'drivers'}>
                        <DriversSection getToken={getToken} onChanged={onChanged} />
                    </TabPanel>
					<TabPanel when={tab === 'facility-hours'}>
                        <FacilityHoursAdmin getToken={getToken} sections={['hours']} />
                    </TabPanel>
                    <TabPanel when={tab === 'appointment-limits'}>
                        <FacilityHoursAdmin getToken={getToken} sections={['limits', 'search']} />
                    </TabPanel>
                    <TabPanel when={tab === 'door-dedications'}>
                        <FacilityHoursAdmin getToken={getToken} sections={['dedications']} />
                    </TabPanel>
                    <TabPanel when={tab === 'chat-groups'}>
                        <ChatGroupsAdmin getToken={getToken} />
                    </TabPanel>
					<TabPanel when={tab === 'facility-directions'}>
						<FacilityDirectionsAdmin getToken={getToken} />
					</TabPanel>
                    <TabPanel when={tab === 'status-colors'}>
                        <StatusColorsAdmin getToken={getToken} />
                    </TabPanel>
                    <TabPanel when={tab === 'rail-panels'}>
                        {/* ⚠️ NO `facilities` PROP AND NO `initialFacilityCode`, unlike its
                            neighbours. This screen is a GRID of every facility on purpose —
                            the question it answers ("which sites have Labor off?") is
                            comparative, and a picker turns that into a tour. The facility
                            list comes back with the config, so the two can never disagree. */}
                        <RailPanelAdmin getToken={getToken} />
                    </TabPanel>
                    <TabPanel when={tab === 'board-screens'}>
                        <BoardConfigAdmin getToken={getToken} facilities={lookups.facilities} />
                    </TabPanel>
                    <TabPanel when={tab === 'driver-checkin'}>
                        <CheckInConfigAdmin getToken={getToken} facilities={lookups.facilities} />
                    </TabPanel>
                    <TabPanel when={tab === 'documents'}>
                        <DocumentConfigAdmin getToken={getToken} facilities={lookups.facilities} initialFacilityCode={currentFacilityCode} />
                    </TabPanel>
                    <TabPanel when={tab === 'ocr-ranking'}>
                        <OcrIdentifierRankAdmin getToken={getToken} lookups={lookups} initialFacilityCode={currentFacilityCode} />
                    </TabPanel>
                    <TabPanel when={tab === 'handouts'}>
                        {/* ⚠️ NO initialFacilityCode, for the same reason as the
                            checklists: a handout is far more often a property of the
                            CONSIGNEE or the CLIENT than of the building. */}
                        <HandoutAdmin getToken={getToken} lookups={lookups} />
                    </TabPanel>
                    <TabPanel when={tab === 'consignees'}>
                        {/* ⚠️ NO initialFacilityCode — a consignee is a place we deliver
                            TO, and has nothing to do with which of our buildings the appointment
                            left from. */}
                        <ConsigneeAdmin getToken={getToken} focusCode={open && tab === 'consignees' ? focusCode : null} />
                    </TabPanel>
                    <TabPanel when={tab === 'departure-checklists'}>
                        {/* ⚠️ NO initialFacilityCode. A checklist is far more often a
                            property of the CLIENT than of the building — "For Hellman,
                            the drivers will need..." names a client — so pre-filtering to
                            the current site would hide the rules that actually apply. */}
                        <DepartureChecklistAdmin getToken={getToken} lookups={lookups} />
                    </TabPanel>
                    {/**
                      * ⛔ THE BRANDING TAB WAS REMOVED 2026-09-07.
                      *
                      * Pete: "it should be one screen in pipeline driving all the
                      * sub apps."
                      *
                      * ** IT WAS A SECOND WRITER TO THE SAME COLUMNS. Pipeline's
                      * Branding screen manages branding per APP across three
                      * tiers (migrations 072/078/079/081/083); this one knew only
                      * the company tier and only the columns that existed in
                      * August -- so saving here wrote a stale subset over
                      * per-line fonts and colours configured elsewhere, silently.
                      *
                      * ⚠️ It also wrote dbo.Company.DisplayName, which belongs to
                      * Companies & Facilities, not to a branding screen.
                      *
                      * ⭐ WHAT WAS WORTH KEEPING WAS PORTED FIRST, not discarded:
                      * the live header preview, size sliders, named font weights,
                      * the font sample, the logo upload-vs-link guidance and the
                      * whole-row reset all now exist in Pipeline's screen.
                      *
                      * ⛔ Do not re-add a branding editor here. The header on this
                      * app reads /api/brand, which resolves from
                      * dbo.vBrandingResolved -- one rule, one writer.
                      */}
                    <TabPanel when={tab === 'door-text'}>
                        <DoorTextConfigAdmin getToken={getToken} facilities={lookups.facilities} initialFacilityCode={currentFacilityCode} />
                    </TabPanel>
                    <TabPanel when={tab === 'appointment-fields'}>
                        <AppointmentFieldsAdmin getToken={getToken} facilities={lookups.facilities} initialFacilityCode={currentFacilityCode} />
                    </TabPanel>
                    <TabPanel when={tab === 'reminder-schedule'}>
                        <ReminderScheduleAdmin getToken={getToken} facilities={lookups.facilities} />
                    </TabPanel>
                    <TabPanel when={tab === 'automated-texts'}>
                        <AutomatedTextsAdmin getToken={getToken} facilities={lookups.facilities} />
                    </TabPanel>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Shared bits
// ============================================================================

function ErrorBanner({ msg }: { msg: string | null }) {
    if (!msg) return null;
    return (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div className="text-destructive">{msg}</div>
        </div>
    );
}

function errMsg(err: unknown): string {
    if (err instanceof ApiCallError) return err.message;
    if (err instanceof Error) return err.message;
    return String(err);
}

// ============================================================================
// Facilities
// ============================================================================

function FacilitiesSection({
    getToken,
    onChanged,
}: {
    getToken: () => Promise<string>;
    onChanged: () => void;
}) {
    const [rows, setRows] = useState<FacilityRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<FacilityRow | 'new' | null>(null);

    const reload = async () => {
        try {
            const token = await getToken();
            setRows(await adminListFacilities(token));
        } catch (err) {
            setError(errMsg(err));
        }
    };
    useEffect(() => { void reload(); }, []);

    const handleToggleActive = async (row: FacilityRow) => {
        try {
            const token = await getToken();
            if (row.DockActive) await adminSoftDeleteFacility(token, row.FacilityCode);
            else await adminRestoreFacility(token, row.FacilityCode);
            await reload();
            onChanged();
        } catch (err) {
            setError(errMsg(err));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Facilities</h3>
                <Button size="sm" onClick={() => setEditing('new')}>
                    <Plus className="h-3.5 w-3.5" />
                    New facility
                </Button>
            </div>

            <ErrorBanner msg={error} />

            {rows === null ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b">
                            <th className="px-2 py-2">Code</th>
                            <th className="px-2 py-2">Name</th>
                            <th className="px-2 py-2">Time Zone</th>
                            <th className="px-2 py-2">WMS</th>
                            <th className="px-2 py-2">Active</th>
                            <th className="px-2 py-2 w-px"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.FacilityCode} className={cn('border-b hover:bg-muted/40', !row.DockActive && 'opacity-50')}>
                                <td className="px-2 py-2 font-mono text-xs">{row.FacilityCode}</td>
                                <td className="px-2 py-2">{row.DisplayName}</td>
                                <td className="px-2 py-2 text-muted-foreground">{row.Timezone}</td>
                                <td className="px-2 py-2 text-muted-foreground">{row.WmsType}</td>
                                <td className="px-2 py-2">
                                    {row.DockActive ? (
                                        <span className="text-xs">Active</span>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">Disabled</span>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => setEditing(row)} aria-label="Edit">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleToggleActive(row)}
                                            aria-label={row.DockActive ? 'Disable' : 'Restore'}
                                        >
                                            {row.DockActive ? (
                                                <Trash2 className="h-3.5 w-3.5" />
                                            ) : (
                                                <RotateCcw className="h-3.5 w-3.5" />
                                            )}
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {editing && (
                <FacilityEditor
                    initial={editing === 'new' ? null : editing}
                    getToken={getToken}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await reload();
                        onChanged();
                    }}
                />
            )}
        </div>
    );
}

function FacilityEditor({
    initial,
    getToken,
    onClose,
    onSaved,
}: {
    initial: FacilityRow | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [availableFacilities, setAvailableFacilities] = useState<{ FacilityCode: string; DisplayName: string; Timezone: string }[]>([]);
    const [selectedCode, setSelectedCode] = useState(initial?.FacilityCode ?? '');
    const [wmsType, setWmsType] = useState<'Deposco' | 'Camelot' | 'Extensiv'>(
        (initial?.WmsType as 'Deposco' | 'Camelot' | 'Extensiv') ?? 'Deposco',
    );
    const [wmsConfig, setWmsConfig] = useState(initial?.WmsConfigJson ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (initial) return; // editing existing — no need to load available list
        (async () => {
            try {
                const token = await getToken();
                const facs = await adminListAvailableFacilities(token);
                setAvailableFacilities(facs);
            } catch (err) {
                setError(errMsg(err));
            }
        })();
    }, [initial, getToken]);



    const handleSave = async () => {
        setError(null);
        if (!initial && !selectedCode) {
            setError('Select a facility from Pipeline.');
            return;
        }
        setBusy(true);
        try {
            const body: UpsertFacilityBody = {
                facilityCode: initial ? initial.FacilityCode : selectedCode,
                wmsType,
                wmsConfigJson: wmsConfig.trim() || null,
                isActive: true,
            };
            const token = await getToken();
            if (initial) {
                await adminUpdateFacility(token, initial.FacilityCode, body);
            } else {
                await adminCreateFacility(token, body);
            }
            onSaved();
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit facility' : 'Enable facility for dock scheduling'}</DialogTitle>
                </DialogHeader>
                <ErrorBanner msg={error} />
                <div className="grid grid-cols-2 gap-3">
                    {initial ? (
                        <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">
                                Facility name, timezone and address are managed in Pipeline (dbo.Facilities).
                                Only WMS settings are editable here.
                            </p>
                            <p className="text-sm font-medium mt-1">
                                {initial.FacilityCode} &mdash; {initial.DisplayName} &mdash; {initial.Timezone}
                            </p>
                        </div>
                    ) : (
                        <div className="col-span-2">
                            <Label htmlFor="f-code">Pipeline Facility</Label>
                            {availableFacilities.length === 0 ? (
                                <p className="text-sm text-muted-foreground mt-1">
                                    All Pipeline facilities are already enabled, or loading...
                                </p>
                            ) : (
                                <Select value={selectedCode} onValueChange={setSelectedCode}>
                                    <SelectTrigger id="f-code" className="mt-1">
                                        <SelectValue placeholder="Select a facility from Pipeline..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableFacilities.map(f => (
                                            <SelectItem key={f.FacilityCode} value={f.FacilityCode}>
                                                {f.DisplayName} ({f.FacilityCode}) &mdash; {f.Timezone}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    )}
                    <div className="col-span-1">
                        <Label htmlFor="f-wms">WMS Type</Label>
                        <Select value={wmsType} onValueChange={(v) => setWmsType(v as typeof wmsType)}>
                            <SelectTrigger id="f-wms" className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Deposco">Deposco</SelectItem>
                                <SelectItem value="Camelot">Camelot</SelectItem>
                                <SelectItem value="Extensiv">Extensiv</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-1" />
                    <div className="col-span-2">
                        <Label htmlFor="f-cfg">WMS Config JSON (optional)</Label>
                        <Textarea
                            id="f-cfg"
                            value={wmsConfig}
                            onChange={(e) => setWmsConfig(e.target.value)}
                            placeholder='{"customField": "value"}'
                            className="mt-1 font-mono text-xs"
                            rows={3}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={handleSave} disabled={busy}>
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {initial ? 'Save' : 'Enable'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Doors
// ============================================================================

function DoorsSection({
    getToken,
    onChanged,
}: {
    getToken: () => Promise<string>;
    onChanged: () => void;
}) {
    const [facilities, setFacilities] = useState<FacilityRow[] | null>(null);
    const [capabilities, setCapabilities] = useState<CapabilityAdminRow[] | null>(null);
    const [selectedFacility, setSelectedFacility] = useState<string | null>(null);
    const [rows, setRows] = useState<DoorAdminRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<DoorAdminRow | 'new' | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const token = await getToken();
                const [facs, caps, me] = await Promise.all([
                    adminListFacilities(token),
                    adminListCapabilities(token),
                    getMe(token).catch(() => null),
                ]);
                setFacilities(facs);
                setCapabilities(caps);
                if (facs.length > 0 && selectedFacility === null) {
                    const def = me?.defaultFacilityCode
                        ? facs.find((f) => f.FacilityCode === me.defaultFacilityCode && f.DockActive)
                        : undefined;
                    const active = def ?? facs.find((f) => f.DockActive) ?? facs[0];
                    setSelectedFacility(active.FacilityCode);
                }
            } catch (err) {
                setError(errMsg(err));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reloadDoors = async () => {
        if (selectedFacility === null) return;
        try {
            const token = await getToken();
            setRows(await adminListDoors(token, selectedFacility));
        } catch (err) {
            setError(errMsg(err));
        }
    };

    useEffect(() => { void reloadDoors(); }, [selectedFacility]);

    const handleToggleActive = async (row: DoorAdminRow) => {
        try {
            const token = await getToken();
            if (row.IsActive) await adminSoftDeleteDoor(token, row.DockDoorId);
            else await adminRestoreDoor(token, row.DockDoorId);
            await reloadDoors();
            onChanged();
        } catch (err) {
            setError(errMsg(err));
        }
    };

    const capMap = new Map((capabilities ?? []).map((c) => [c.CapabilityId, c]));

    return (
        <div>
            <div className="flex items-center justify-between mb-3 gap-3">
                <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-muted-foreground">Facility</span>
                    {facilities && (
                        <Select
                            value={selectedFacility !== null ? String(selectedFacility) : ''}
                            onValueChange={(v) => setSelectedFacility(v)}
                        >
                            <SelectTrigger className="w-64">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {facilities.map((f) => (
                                    <SelectItem key={f.FacilityCode} value={f.FacilityCode}>
                                        {f.DisplayName}
                                        {!f.DockActive && <span className="text-muted-foreground ml-1">(disabled)</span>}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>
                <Button size="sm" onClick={() => setEditing('new')} disabled={selectedFacility === null}>
                    <Plus className="h-3.5 w-3.5" />
                    New door
                </Button>
            </div>

            <ErrorBanner msg={error} />

            {rows === null || capabilities === null ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                </div>
            ) : rows.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                    No doors configured for this facility yet.
                </div>
            ) : (
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b">
                            <th className="px-2 py-2 w-16">Code</th>
                            <th className="px-2 py-2">Name</th>
                            <th className="px-2 py-2">Group</th>
                            <th className="px-2 py-2">Order</th>
                            <th className="px-2 py-2">Capabilities</th>
                            <th className="px-2 py-2">Active</th>
                            <th className="px-2 py-2 w-px"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.DockDoorId} className={cn('border-b hover:bg-muted/40', !row.IsActive && 'opacity-50')}>
                                <td className="px-2 py-2 font-mono text-xs">{row.Code}</td>
                                <td className="px-2 py-2">{row.Name}</td>
                                <td className="px-2 py-2 text-muted-foreground">{row.DoorGroup ?? '—'}</td>
                                <td className="px-2 py-2 text-tabular text-xs text-muted-foreground">{row.DisplayOrder}</td>
                                <td className="px-2 py-2">
                                    {row.CapabilityIds.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {row.CapabilityIds.map((id) => {
                                                const c = capMap.get(id);
                                                return (
                                                    <span
                                                        key={id}
                                                        className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground font-medium uppercase"
                                                    >
                                                        {c?.Code ?? id}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    {row.IsActive ? <span className="text-xs">Active</span> : <span className="text-xs text-muted-foreground">Disabled</span>}
                                </td>
                                <td className="px-2 py-2">
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => setEditing(row)} aria-label="Edit">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleToggleActive(row)}
                                            aria-label={row.IsActive ? 'Disable' : 'Restore'}
                                        >
                                            {row.IsActive ? <Trash2 className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {editing && selectedFacility !== null && capabilities !== null && (
                <DoorEditor
                    facilityCode={selectedFacility}
                    initial={editing === 'new' ? null : editing}
                    capabilities={capabilities}
                    getToken={getToken}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await reloadDoors();
                        onChanged();
                    }}
                />
            )}
        </div>
    );
}

export function DoorEditor({
    facilityCode,
    initial,
    capabilities,
    getToken,
    onClose,
    onSaved,
}: {
    facilityCode: string;
    initial: DoorAdminRow | null;
    capabilities: CapabilityAdminRow[];
    getToken: () => Promise<string>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [code, setCode] = useState(initial?.Code ?? '');
    const [name, setName] = useState(initial?.Name ?? '');
    const [doorGroup, setDoorGroup] = useState(initial?.DoorGroup ?? '');
    const [displayOrder, setDisplayOrder] = useState(initial?.DisplayOrder ?? 0);
    const [maxConcurrent, setMaxConcurrent] = useState(initial?.MaxConcurrent ?? 1);
    const [allowedDirection, setAllowedDirection] = useState<'INBOUND' | 'OUTBOUND' | 'BOTH'>(
        initial?.AllowedDirection ?? 'BOTH',
    );
    const [capIds, setCapIds] = useState<number[]>(initial?.CapabilityIds ?? []);
    const [isActive, setIsActive] = useState<boolean>(initial?.IsActive ?? true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleCap = (id: number) => {
        setCapIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleSave = async () => {
        setError(null);
        if (!code.trim() || !name.trim()) {
            setError('Code and Name are required.');
            return;
        }
        setBusy(true);
        try {
            const body: UpsertDoorBody = {
                facilityCode,
                code: code.trim(),
                name: name.trim(),
                doorGroup: doorGroup.trim() || null,
                displayOrder,
                maxConcurrent,
                allowedDirection,
                capabilityIds: capIds,
                isActive,
            };
            const token = await getToken();
            if (initial) {
                await adminUpdateDoor(token, initial.DockDoorId, body);
            } else {
                await adminCreateDoor(token, body);
            }
            onSaved();
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{initial ? `Edit ${initial.Code}` : 'New dock door'}</DialogTitle>
                </DialogHeader>
                <ErrorBanner msg={error} />
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-1">
                        <Label htmlFor="d-code">Code</Label>
                        <Input id="d-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="DD09" className="mt-1 font-mono" />
                    </div>
                    <div className="col-span-1">
                        <Label htmlFor="d-name">Name</Label>
                        <Input id="d-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Refrigerated Bay 1" className="mt-1" />
                    </div>
                    <div className="col-span-1">
                        <Label htmlFor="d-group">Door Group (optional)</Label>
                        <Input id="d-group" value={doorGroup} onChange={(e) => setDoorGroup(e.target.value)} placeholder="Receiving" className="mt-1" />
                    </div>
                    <div className="col-span-1">
                        <Label htmlFor="d-direction">Allowed Direction</Label>
                        <select
                            id="d-direction"
                            value={allowedDirection}
                            onChange={(e) => setAllowedDirection(e.target.value as 'INBOUND' | 'OUTBOUND' | 'BOTH')}
                            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        >
                            <option value="BOTH">Both (inbound + outbound)</option>
                            <option value="INBOUND">Inbound only</option>
                            <option value="OUTBOUND">Outbound only</option>
                        </select>
                    </div>
                    <div className="col-span-1 grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="d-order">Display Order</Label>
                            <Input
                                id="d-order"
                                type="number"
                                value={displayOrder}
                                onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
                                className="mt-1 text-tabular"
                            />
                        </div>
                        <div>
                            <Label htmlFor="d-max">Max Concurrent</Label>
                            <Input
                                id="d-max"
                                type="number"
                                min={1}
                                value={maxConcurrent}
                                onChange={(e) => setMaxConcurrent(parseInt(e.target.value, 10) || 1)}
                                className="mt-1 text-tabular"
                            />
                        </div>
                    </div>
                    <div className="col-span-2">
                        <Label>Capabilities</Label>
                        <div className="mt-1.5 flex flex-wrap gap-2">
                            {capabilities.length === 0 ? (
                                <span className="text-xs text-muted-foreground">No capabilities defined. Create one in the Capabilities tab first.</span>
                            ) : (
                                capabilities.map((c) => {
                                    const active = capIds.includes(c.CapabilityId);
                                    return (
                                        <button
                                            type="button"
                                            key={c.CapabilityId}
                                            onClick={() => toggleCap(c.CapabilityId)}
                                            className={cn(
                                                'px-3 py-1 rounded-full border text-xs font-medium transition-colors',
                                                active
                                                    ? 'bg-primary text-primary-foreground border-primary'
                                                    : 'bg-background hover:bg-accent border-input',
                                            )}
                                        >
                                            {c.Name}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
                <label className="flex items-center gap-2 pt-3 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        className="h-4 w-4 rounded border-input"
                    />
                    <span className="text-sm">
                        Active{' '}
                        <span className="text-muted-foreground">
                            — inactive doors are hidden from the calendar but keep their appointment history
                        </span>
                    </span>
                </label>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={handleSave} disabled={busy}>
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Capabilities
// ============================================================================

function CapabilitiesSection({
    getToken,
    onChanged,
}: {
    getToken: () => Promise<string>;
    onChanged: () => void;
}) {
    const [rows, setRows] = useState<CapabilityAdminRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<CapabilityAdminRow | 'new' | null>(null);

    const reload = async () => {
        try {
            const token = await getToken();
            setRows(await adminListCapabilities(token));
        } catch (err) {
            setError(errMsg(err));
        }
    };
    useEffect(() => { void reload(); }, []);

    const [pendingDelete, setPendingDelete] = useState<CapabilityAdminRow | null>(null);
    const runDelete = async (row: CapabilityAdminRow) => {
        try {
            const token = await getToken();
            await adminDeleteCapability(token, row.CapabilityId);
            await reload();
            onChanged();
        } catch (err) {
            setError(errMsg(err));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Capabilities</h3>
                <Button size="sm" onClick={() => setEditing('new')}>
                    <Plus className="h-3.5 w-3.5" />
                    New capability
                </Button>
            </div>
            <ErrorBanner msg={error} />
            {rows === null ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b">
                            <th className="px-2 py-2">Code</th>
                            <th className="px-2 py-2">Name</th>
                            <th className="px-2 py-2">Description</th>
                            <th className="px-2 py-2 w-px"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.CapabilityId} className="border-b hover:bg-muted/40">
                                <td className="px-2 py-2 font-mono text-xs">{row.Code}</td>
                                <td className="px-2 py-2">{row.Name}</td>
                                <td className="px-2 py-2 text-muted-foreground text-xs">{row.Description ?? '—'}</td>
                                <td className="px-2 py-2">
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => setEditing(row)} aria-label="Edit">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => setPendingDelete(row)} aria-label="Delete">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {editing && (
                <CapabilityEditor
                    initial={editing === 'new' ? null : editing}
                    getToken={getToken}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await reload();
                        onChanged();
                    }}
                />
            )}

            {pendingDelete && (
                <ConfirmDialog
                    open={true}
                    destructive
                    title="Delete capability?"
                    message={`Delete capability "${pendingDelete.Name}"? This is permanent.`}
                    confirmLabel="Delete capability"
                    onConfirm={() => { const r = pendingDelete; setPendingDelete(null); void runDelete(r); }}
                    onCancel={() => setPendingDelete(null)}
                />
            )}
        </div>
    );
}

function CapabilityEditor({
    initial,
    getToken,
    onClose,
    onSaved,
}: {
    initial: CapabilityAdminRow | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [code, setCode] = useState(initial?.Code ?? '');
    const [name, setName] = useState(initial?.Name ?? '');
    const [description, setDescription] = useState(initial?.Description ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        setError(null);
        if (!code.trim() || !name.trim()) {
            setError('Code and Name are required.');
            return;
        }
        setBusy(true);
        try {
            const body: UpsertCapabilityBody = {
                code: code.trim().toUpperCase(),
                name: name.trim(),
                description: description.trim() || null,
            };
            const token = await getToken();
            if (initial) {
                await adminUpdateCapability(token, initial.CapabilityId, body);
            } else {
                await adminCreateCapability(token, body);
            }
            onSaved();
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit capability' : 'New capability'}</DialogTitle>
                </DialogHeader>
                <ErrorBanner msg={error} />
                <div className="grid gap-3">
                    <div>
                        <Label htmlFor="c-code">Code</Label>
                        <Input
                            id="c-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="REFRIGERATED"
                            className="mt-1 font-mono"
                        />
                    </div>
                    <div>
                        <Label htmlFor="c-name">Name</Label>
                        <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Refrigerated" className="mt-1" />
                    </div>
                    <div>
                        <Label htmlFor="c-desc">Description (optional)</Label>
                        <Textarea
                            id="c-desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Temperature-controlled door for chilled or frozen loads."
                            className="mt-1"
                            rows={2}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={handleSave} disabled={busy}>
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Read-only lists (Carriers / Clients)
// ============================================================================

function ReadOnlyList({
    title,
    note,
    rows,
    initialFilter = null,
}: {
    title: string;
    note: string;
    rows: { code: string; name: string }[];
    /** ⭐ F130: opened from a rail card — start filtered to that code. */
    initialFilter?: string | null;
}) {
    const [filter, setFilter] = useState(initialFilter ?? '');
    useEffect(() => { if (initialFilter) setFilter(initialFilter); }, [initialFilter]);
    const filtered = rows.filter(
        (r) =>
            r.code.toLowerCase().includes(filter.toLowerCase()) ||
            r.name.toLowerCase().includes(filter.toLowerCase()),
    );

    return (
        <div>
            <div className="mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">{title}</h3>
                <p className="text-xs text-muted-foreground">{note}</p>
            </div>
            <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`Filter ${title.toLowerCase()}…`}
                className="mb-3"
            />
            <div className="text-xs text-muted-foreground mb-2">
                {filtered.length} of {rows.length}
            </div>
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b">
                        <th className="px-2 py-2 w-32">Code</th>
                        <th className="px-2 py-2">Name</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((r) => (
                        <tr key={r.code} className="border-b hover:bg-muted/40">
                            <td className="px-2 py-2 font-mono text-xs">{r.code}</td>
                            <td className="px-2 py-2">{r.name}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ============================================================================
// Carriers (editable CRUD — sourced from dock.Carrier table)
// ============================================================================

function CarriersSection({
    getToken,
    onChanged,
    focusCode = null,
}: {
    getToken: () => Promise<string>;
    onChanged: () => void;
    /** ⭐ F130: opened from a rail card — filter to that carrier and open its editor. */
    focusCode?: string | null;
}) {
    const [rows, setRows] = useState<CarrierAdminRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<CarrierAdminRow | 'new' | null>(null);
    const [filter, setFilter] = useState(focusCode ?? '');
    /* ⭐ F130: once the list is in, the focused carrier's editor opens (exact code only —
       a near match is left to the filter, never guessed at). */
    useEffect(() => {
        if (!focusCode) return;
        setFilter(focusCode);
        const hit = rows?.find(r => r.CarrierCode.toLowerCase() === focusCode.toLowerCase());
        if (hit) setEditing(hit);
    }, [focusCode, rows]);

    const reload = async () => {
        try {
            const token = await getToken();
            setRows(await adminListCarriers(token));
        } catch (err) {
            setError(errMsg(err));
        }
    };
    useEffect(() => { void reload(); }, []);

    const handleToggleActive = async (row: CarrierAdminRow) => {
        try {
            const token = await getToken();
            if (row.IsActive) await adminSoftDeleteCarrier(token, row.CarrierId);
            else await adminRestoreCarrier(token, row.CarrierId);
            await reload();
            onChanged();
        } catch (err) {
            setError(errMsg(err));
        }
    };

    const filtered = rows?.filter(
        (r) =>
            r.CarrierCode.toLowerCase().includes(filter.toLowerCase()) ||
            r.CarrierName.toLowerCase().includes(filter.toLowerCase()),
    );

    return (
        <div>
            <div className="mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Carriers
                </h3>
                <p className="text-xs text-muted-foreground">
                    Manage the list of carriers schedulers can pick from. Carrier code is the
                    business key (e.g., FEDEX, UPS, XPO) and is immutable once created.
                </p>
            </div>

            <ErrorBanner msg={error} />

            <div className="flex items-center gap-2 mb-3">
                <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter carriers…"
                    className="flex-1"
                />
                <Button size="sm" onClick={() => setEditing('new')}>
                    <Plus className="h-3.5 w-3.5" />
                    New carrier
                </Button>
            </div>

            {rows === null ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                </div>
            ) : filtered && filtered.length > 0 ? (
                <>
                    <div className="text-xs text-muted-foreground mb-2">
                        {filtered.length} of {rows.length}
                    </div>
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b">
                                <th className="px-2 py-2 w-24">Code</th>
                                <th className="px-2 py-2">Name</th>
                                <th className="px-2 py-2">Email</th>
                                <th className="px-2 py-2">Phone</th>
                                <th className="px-2 py-2 w-20">Active</th>
                                <th className="px-2 py-2 w-px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row) => (
                                <tr
                                    key={row.CarrierId}
                                    className={cn('border-b hover:bg-muted/40', !row.IsActive && 'opacity-50')}
                                >
                                    <td className="px-2 py-2 font-mono text-xs">{row.CarrierCode}</td>
                                    <td className="px-2 py-2">{row.CarrierName}</td>
                                    <td className="px-2 py-2 text-muted-foreground text-xs">
                                        {row.ContactEmail ?? '—'}
                                    </td>
                                    <td className="px-2 py-2 text-muted-foreground text-xs">
                                        {row.ContactPhone ?? '—'}
                                    </td>
                                    <td className="px-2 py-2">
                                        {row.IsActive ? (
                                            <span className="text-xs">Active</span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Disabled</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-2">
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setEditing(row)}
                                                aria-label="Edit"
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleToggleActive(row)}
                                                aria-label={row.IsActive ? 'Disable' : 'Restore'}
                                            >
                                                {row.IsActive ? (
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                ) : (
                                                    <RotateCcw className="h-3.5 w-3.5" />
                                                )}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                    {filter ? 'No carriers match the filter.' : 'No carriers configured.'}
                </div>
            )}

            {editing && (
                <CarrierEditor
                    initial={editing === 'new' ? null : editing}
                    getToken={getToken}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await reload();
                        onChanged();
                    }}
                />
            )}
        </div>
    );
}

function CarrierEditor({
    initial,
    getToken,
    onClose,
    onSaved,
}: {
    initial: CarrierAdminRow | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [carrierCode, setCarrierCode] = useState(initial?.CarrierCode ?? '');
    const [carrierName, setCarrierName] = useState(initial?.CarrierName ?? '');
    const [contactEmail, setContactEmail] = useState(initial?.ContactEmail ?? '');
    const [contactPhone, setContactPhone] = useState(initial?.ContactPhone ?? '');
    const [notes, setNotes] = useState(initial?.Notes ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        setError(null);
        if (!carrierCode.trim() || !carrierName.trim()) {
            setError('Carrier Code and Name are required.');
            return;
        }
        setBusy(true);
        try {
            const body: UpsertCarrierBody = {
                // On edit, the Code field is disabled/immutable in the UI anyway --
                // send back the ORIGINAL stored value verbatim rather than
                // re-deriving it from the input state. Previously this always
                // re-uppercased on submit (carrierCode.trim().toUpperCase()), which
                // broke for any carrier whose code was stored in mixed case (e.g.
                // "FedEx") -- the backend's immutability check compares against the
                // exact stored value, so "FedEx" vs "FEDEX" looked like an attempted
                // change and got rejected, even though nothing was actually edited.
                carrierCode: initial ? initial.CarrierCode : carrierCode.trim().toUpperCase(),
                carrierName: carrierName.trim(),
                contactEmail: contactEmail.trim() || null,
                contactPhone: contactPhone.trim() || null,
                notes: notes.trim() || null,
            };
            const token = await getToken();
            if (initial) {
                await adminUpdateCarrier(token, initial.CarrierId, body);
            } else {
                await adminCreateCarrier(token, body);
            }
            onSaved();
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{initial ? `Edit ${initial.CarrierCode}` : 'New carrier'}</DialogTitle>
                </DialogHeader>
                <ErrorBanner msg={error} />
                <div className="grid gap-3">
                    <div>
                        <Label htmlFor="car-code">
                            Code{initial && <span className="text-xs text-muted-foreground ml-1">(immutable)</span>}
                        </Label>
                        <Input
                            id="car-code"
                            value={carrierCode}
                            onChange={(e) => setCarrierCode(e.target.value.toUpperCase())}
                            placeholder="FEDEX"
                            className="mt-1 font-mono"
                            disabled={initial !== null}
                        />
                    </div>
                    <div>
                        <Label htmlFor="car-name">Name</Label>
                        <Input
                            id="car-name"
                            value={carrierName}
                            onChange={(e) => setCarrierName(e.target.value)}
                            placeholder="FedEx Freight"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label htmlFor="car-email">Contact Email (optional)</Label>
                        <Input
                            id="car-email"
                            type="email"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            placeholder="dispatch@fedex.com"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label htmlFor="car-phone">Contact Phone (optional)</Label>
                        <Input
                            id="car-phone"
                            type="tel"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="+1 555 555 5555"
                            className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Fallback for the appointment reminder&apos;s text — used only when the specific
                            dispatcher contact has no phone of their own on file.
                        </p>
                    </div>
                    <div>
                        <Label htmlFor="car-notes">Notes (optional)</Label>
                        <Textarea
                            id="car-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Preferred dock door, special instructions, etc."
                            className="mt-1"
                            rows={3}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={busy}>
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


